import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";
import { FaCalendarPlus } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { getCalendarEvents } from "../../api/CALENDAR/calendar";
import { useUsers } from "../../hooks/useGetUsers";
import NewAgendaEvent from "../../components/Modals/Create/NewAgendaEvent";
import EditAgendaEvent from "../../components/Modals/Update/EditAgendaEvent";
import { toast } from "react-toastify";
import { useUpdateDiary } from "../../hooks/CALENDAR/useUpdateDiary";
import { useUpdateTicket } from "../../hooks/TICKET/useUpdateTicket";

const CALENDAR_USERS_KEY = "calendar_selectedUserIds";

// colorMap : Map<userId, colorString> — sert de fallback si event.user.color est absent
function mapToFullCalendarEvents(event, colorMap = new Map()) {
  let title = event.label || event.description || "(sans titre)";
  if (event.type === "ticket_intervention" && event.ticket) {
    const client = event.ticket.client?.name;
    title = client ? `🎫 ${client} — ${event.ticket.title}` : `🎫 ${event.ticket.title}`;
  } else if (event.type === "project_intervention" && event.project) {
    const client = event.project.client?.name;
    title = client ? `📁 ${client} — ${event.project.title}` : `📁 ${event.project.title}`;
  } else if (event.type === "ticket") {
    const client = event.client?.name;
    const label = event.label || event.description || "(sans titre)";
    title = client ? `🎫 ${client} — ${label}` : `🎫 ${label}`;
  }

  const resolveColor = (user) =>
    (user?.id && colorMap.get(user.id)) || user?.color || "#4e73df";

  const base = {
    title,
    start: event.start_date,
    end: event.end_date || undefined,
    editable: event.type === "diary" || event.type === "ticket",
    extendedProps: { original: event },
  };

  if (event.type === "ticket" && event.users?.length) {
    return event.users.map((u) => ({
      ...base,
      id: `ticket-${event.id}-user-${u.id}`,
      backgroundColor: resolveColor(u),
      borderColor: resolveColor(u),
      extendedProps: { original: event, userId: u.id },
    }));
  }

  const color = resolveColor(event.user);
  return [{
    ...base,
    id: `${event.type}-${event.id}`,
    backgroundColor: color,
    borderColor: color,
  }];
}

// Convertit un dateStr FullCalendar ("2024-06-04T09:00:00+02:00") en UTC ISO avec Z.
const toUTCISO = (dateStr) => dateStr ? new Date(dateStr).toISOString().slice(0, 19) + "Z" : null;

const Calendrier = ({ titre }) => {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const calendarRef = useRef(null);
  const queryClient = useQueryClient();
  const updateDiaryMutation = useUpdateDiary();
  const updateTicketMutation = useUpdateTicket();
  const currentUserId = user?.data?.id;

  const restored = location.state;

  const [range, setRange] = useState({ start: null, end: null });
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaultDate, setCreateDefaultDate] = useState("");
  const [editEvent, setEditEvent] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem(CALENDAR_USERS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return new Set(parsed);
      }
    } catch {}
    return new Set(currentUserId ? [currentUserId] : []);
  });

  // Persiste la sélection des calendriers entre les navigations
  useEffect(() => {
    try {
      sessionStorage.setItem(CALENDAR_USERS_KEY, JSON.stringify([...selectedUserIds]));
    } catch {}
  }, [selectedUserIds]);

  // Liste complète des utilisateurs pour le sélecteur
  const { data: usersData } = useUsers();
  const allUsers = useMemo(() => usersData?.data ?? [], [usersData]);

  // Une requête par utilisateur sélectionné — chacune a sa propre queryKey donc son propre cache
  const selectedUserIdsArray = useMemo(() => Array.from(selectedUserIds), [selectedUserIds]);

  const queries = useQueries({
    queries: selectedUserIdsArray.map((userId) => ({
      queryKey: ["calendarEvents", range.start, range.end, userId],
      queryFn: () => getCalendarEvents(range.start, range.end, [userId]),
      enabled: !!range.start && !!range.end,
      staleTime: 5 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    })),
  });

  // userId → résultat de query (pour les indicateurs de chargement par utilisateur)
  const queryByUserId = useMemo(() => {
    const map = new Map();
    selectedUserIdsArray.forEach((uid, i) => map.set(uid, queries[i]));
    return map;
  }, [selectedUserIdsArray, queries]);

  // Déduplique les événements provenant de plusieurs requêtes.
  // Pour les tickets, fusionne les `users` de toutes les requêtes afin d'afficher
  // un événement par utilisateur assigné même si le backend retourne une vue partielle.
  const allEvents = useMemo(() => {
    const ticketMap = new Map();
    const seen = new Set();
    const result = [];
    for (const q of queries) {
      if (!q.isSuccess) continue;
      for (const ev of (q.data?.data?.events ?? [])) {
        if (ev.type === "ticket") {
          if (!ticketMap.has(ev.id)) {
            ticketMap.set(ev.id, { ...ev, users: [...(ev.users ?? [])] });
          } else {
            const existing = ticketMap.get(ev.id);
            const existingIds = new Set(existing.users.map((u) => u.id));
            for (const u of (ev.users ?? [])) {
              if (!existingIds.has(u.id)) existing.users.push(u);
            }
          }
        } else {
          const key = `${ev.type}-${ev.id}`;
          if (!seen.has(key)) {
            seen.add(key);
            result.push(ev);
          }
        }
      }
    }
    return [...ticketMap.values(), ...result];
  }, [queries]);

  const isAnyFetching = queries.some((q) => q.isFetching);

  const USER_PALETTE = ["#4e73df","#1cc88a","#36b9cc","#f6c23e","#e74a3b","#fd7e14","#6610f2","#20c997","#d63384","#0dcaf0"];

  // Seedée depuis allUsers (source fiable, toujours disponible) puis complétée par les événements
  const userColorMap = useMemo(() => {
    const map = new Map();
    for (const u of allUsers) {
      if (u?.id && u.color) map.set(u.id, u.color);
    }
    for (const q of queries) {
      if (!q.isSuccess) continue;
      for (const ev of (q.data?.data?.events ?? [])) {
        const users = ev.users?.length ? ev.users : (ev.user ? [ev.user] : []);
        users.forEach((u) => { if (u?.id && u.color) map.set(u.id, u.color); });
      }
    }
    return map;
  }, [queries, allUsers]);

  // Pour les tickets multi-assignés : n'afficher que les couleurs des utilisateurs sélectionnés
  const events = useMemo(() =>
    allEvents.flatMap((ev) => {
      if (ev.type === "ticket" && ev.users?.length) {
        const visibleUsers = ev.users.filter((u) => selectedUserIds.has(u.id));
        return mapToFullCalendarEvents(
          { ...ev, users: visibleUsers.length ? visibleUsers : ev.users },
          userColorMap
        );
      }
      return mapToFullCalendarEvents(ev, userColorMap);
    }),
    [allEvents, selectedUserIds, userColorMap]
  );

  const toggleUser = useCallback((userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        if (next.size > 1) next.delete(userId); // conserver au moins un calendrier affiché
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleDatesSet = useCallback((dateInfo) => {
    const start = dateInfo.startStr.slice(0, 10);
    const end = dateInfo.endStr.slice(0, 10);
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  const handleDateClick = (info) => {
    if (!hasPermission("CREATE_EVENT")) return;
    setCreateDefaultDate(info.dateStr);
    setShowCreate(true);
  };

  const handleEventClick = (info) => {
    const original = info.event.extendedProps.original;
    const api = calendarRef.current?.getApi();
    const calendarState = api
      ? { calendarDate: api.getDate().toISOString(), calendarView: api.view.type }
      : {};
    const navState = {
      state: {
        from: location.pathname,
        listState: { scrollY: window.scrollY, ...calendarState },
      },
    };
    if (original.type === "ticket_intervention" && original.ticket?.id) {
      navigate(`/ticket/${original.ticket.id}`, navState);
      return;
    }
    if (original.type === "ticket") {
      navigate(`/ticket/${original.id}`, navState);
      return;
    }
    if (original.type === "project_intervention" && original.project?.id) {
      navigate(`/projet/${original.project.id}`, navState);
      return;
    }
    if (original.type === "diary") {
      const ownerId = original.user?.id != null ? Number(original.user.id) : null;
      const canEdit = hasPermission("EDIT_ALL_EVENTS")
        || ownerId === null
        || ownerId === Number(currentUserId);
      if (!canEdit) return;
      setEditEvent(original);
    }
  };

  const handleEventDrop = useCallback(async (info) => {
    const original = info.event.extendedProps.original;
    const newStart = toUTCISO(info.event.startStr);
    const hasOriginalEnd = !!original.end_date;
    const newEnd = hasOriginalEnd ? toUTCISO(info.event.endStr) : undefined;

    if (original.type === "diary") {
      const ownerId = original.user?.id != null ? Number(original.user.id) : null;
      const canEdit = hasPermission("EDIT_ALL_EVENTS") || ownerId === null || ownerId === Number(currentUserId);
      if (!canEdit) { info.revert(); toast.error("Vous n'avez pas la permission de modifier cet événement"); return; }
      try {
        await updateDiaryMutation.mutateAsync({
          id: original.id,
          data: {
            label: original.label,
            start_date: newStart,
            ...(newEnd !== undefined ? { end_date: newEnd } : {}),
            description: original.description,
            participant_ids: (original.participants || []).map((p) => p.id).filter(Boolean),
          },
        });
      } catch {
        info.revert();
        toast.error("Erreur lors du déplacement");
      }
      return;
    }

    if (original.type === "ticket") {
      try {
        await updateTicketMutation.mutateAsync({
          id: original.id,
          ticketData: {
            date_debut: newStart,
            ...(newEnd !== undefined ? { date_fin: newEnd } : {}),
          },
        });
        queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      } catch {
        info.revert();
        toast.error("Erreur lors du déplacement");
      }
    }
  }, [currentUserId, hasPermission, updateDiaryMutation, updateTicketMutation, queryClient]);

  const handleEventResize = useCallback(async (info) => {
    const original = info.event.extendedProps.original;
    const newStart = toUTCISO(info.event.startStr);
    const newEnd = toUTCISO(info.event.endStr);

    if (original.type === "diary") {
      const ownerId = original.user?.id != null ? Number(original.user.id) : null;
      const canEdit = hasPermission("EDIT_ALL_EVENTS") || ownerId === null || ownerId === Number(currentUserId);
      if (!canEdit) { info.revert(); toast.error("Vous n'avez pas la permission de modifier cet événement"); return; }
      try {
        await updateDiaryMutation.mutateAsync({
          id: original.id,
          data: {
            label: original.label,
            start_date: newStart,
            end_date: newEnd,
            description: original.description,
            participant_ids: (original.participants || []).map((p) => p.id).filter(Boolean),
          },
        });
      } catch {
        info.revert();
        toast.error("Erreur lors du redimensionnement");
      }
      return;
    }

    if (original.type === "ticket") {
      try {
        await updateTicketMutation.mutateAsync({
          id: original.id,
          ticketData: { date_debut: newStart, date_fin: newEnd },
        });
        queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      } catch {
        info.revert();
        toast.error("Erreur lors du redimensionnement");
      }
    }
  }, [currentUserId, hasPermission, updateDiaryMutation, updateTicketMutation, queryClient]);

  return (
    <>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>
        {hasPermission("CREATE_EVENT") && (
          <button
            className="btn btn-primary btn-icon-split"
            onClick={() => { setCreateDefaultDate(""); setShowCreate(true); }}
          >
            <span className="icon body-color">
              <FaCalendarPlus />
            </span>
            <span className="text">Ajouter un événement</span>
          </button>
        )}
      </div>

      {/* Sélecteur de calendriers */}
      {allUsers.length > 0 && (
        <div className="card shadow-sm mb-3">
          <div className="card-body py-2 px-3">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted fw-semibold me-1" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Calendriers affichés
              </small>
              {allUsers.map((u, idx) => {
                const isSelected = selectedUserIds.has(u.id);
                const query = queryByUserId.get(u.id);
                const isLoading = isSelected && (query?.isLoading || query?.isFetching);
                const color = userColorMap.get(u.id) || u.color || USER_PALETTE[idx % USER_PALETTE.length];
                return (
                  <button
                    key={u.id}
                    onClick={() => toggleUser(u.id)}
                    className="d-flex align-items-center gap-1"
                    style={{
                      border: `2px solid ${color}`,
                      borderRadius: "0.25rem",
                      color: isSelected ? "#fff" : color,
                      backgroundColor: isSelected ? color : "transparent",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      fontWeight: isSelected ? 600 : 400,
                      padding: "0.2rem 0.55rem",
                      transition: "all 0.15s",
                      lineHeight: 1.5,
                    }}
                  >
                    {isLoading && (
                      <Spinner
                        animation="border"
                        style={{ width: 9, height: 9, borderWidth: "0.12em", flexShrink: 0, color: isSelected ? "#fff" : color }}
                      />
                    )}
                    {u.surname} {u.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card border-left-primary shadow mb-4">
        <div className="card-body p-3">
          {isAnyFetching && (
            <div className="text-center text-muted py-2 small">Chargement des événements…</div>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locale={frLocale}
            timeZone="local"
            initialView={restored?.calendarView || "timeGridWeek"}
            initialDate={restored?.calendarDate || undefined}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            events={events}
            datesSet={handleDatesSet}
            dateClick={hasPermission("CREATE_EVENT") ? handleDateClick : undefined}
            eventClick={handleEventClick}
            editable
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            nowIndicator
            hiddenDays={[0]}
            allDaySlot={false}
            slotMinTime="08:00:00"
            slotMaxTime="19:00:00"
            height="auto"
            eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
            slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          />
        </div>
      </div>

      <NewAgendaEvent
        showNewAgendaEvent={showCreate}
        setShowNewAgendaEvent={setShowCreate}
        defaultDate={createDefaultDate}
      />

      <EditAgendaEvent
        event={editEvent}
        show={!!editEvent}
        onHide={() => setEditEvent(null)}
        canDelete={
          editEvent && (
            (editEvent.user?.id == null || Number(editEvent.user.id) === Number(currentUserId))
              ? hasPermission("DELETE_EVENT")
              : hasPermission("DELETE_ALL_EVENTS")
          )
        }
      />
    </>
  );
};

export default Calendrier;
